import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest, object = authed
  const [tenant, setTenant] = useState(null);
  const [branch, setBranch] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [moduleAccess, setModuleAccess] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [activeTenantId, setActiveTenantIdState] = useState(
    () => localStorage.getItem("nexusbiz_active_tenant") || null
  );

  // Single hydration point for every auth entry flow (login, OAuth callback, reload)
  const hydrate = useCallback((data) => {
    setUser(data.user);
    setTenant(data.tenant);
    setBranch(data.branch);
    setPermissions(data.permissions || {});
    setModuleAccess(data.module_access || {});
    setSubscription(data.subscription || null);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      hydrate(data);
    } catch {
      setUser(false);
      setTenant(null);
      setBranch(null);
      setPermissions({});
      setModuleAccess({});
      setSubscription(null);
    }
  }, [hydrate]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) return;
    checkAuth();
  }, [checkAuth]);

  const setActiveTenantId = useCallback((id) => {
    setActiveTenantIdState(id);
    if (id) localStorage.setItem("nexusbiz_active_tenant", id);
    else localStorage.removeItem("nexusbiz_active_tenant");
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("nexusbiz_active_tenant");
    setActiveTenantIdState(null);
    setUser(false);
    setTenant(null);
    setBranch(null);
    setPermissions({});
    setModuleAccess({});
    setSubscription(null);
  }, []);

  const canAccessModule = (m) => {
    if (!user || user === false) return false;
    if (user.role === "system_admin") return true;
    if (m === "subscription" && user.role === "owner") return true;
    if ((moduleAccess[m] ?? "manage") === "none") return false;
    const pm = subscription?.modules;
    if (pm && !pm.includes(m)) return false;
    return true;
  };

  return (
    <AuthContext.Provider value={{
      user, tenant, branch, permissions, subscription, activeTenantId, moduleAccess,
      setUser, setTenant, setBranch, setPermissions, setSubscription, setActiveTenantId, setModuleAccess,
      hydrate, checkAuth, logout, canAccessModule,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
